import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { filter, merge } from 'rxjs';
import { get } from 'lodash-es';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { SignalRService } from 'app/core/services/signalr.service';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { FormlyErrorComponent } from '../formly-error.component';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { debugLog } from '../../utils/debug-logger';
import type { SigningDocumentMetadata, SigningEntry, SigningStatus } from './signing.types';
import { SigningStatusEnum } from './signing.types';
import { StatusBadgeComponent } from '../status-badge.component';
import type { BadgeVariant } from '../status-badge.types';
import { canMutateField } from './readonly/readonly.types';

/**
 * Formly field 'signing-sign' — the signer (e.g. onboarder) views and signs a document.
 *
 * Field value = reference to a signing entry (SigningEntryRef or full entry; only
 * `.id` is used). The live entry is fetched on init and re-fetched on SignalR
 * entry changes for the signing module, so the status updates without a page
 * reload. When `props.required`, the field stays invalid until the signing
 * entry's status is 'ondertekend'.
 */
@Component({
  selector: 'formly-field-signing-sign',
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatIconModule,
    MatButtonModule,
    MatTooltip,
    MatProgressSpinner,
    FormlyErrorComponent,
    FormlyDataCyDirective,
    StatusBadgeComponent,
  ],
  template: `
    <div class="flex flex-col mb-4" [formlyDataCy]="field">
      @if (props.label) {
        <span class="font-medium inline-block mb-2"> {{ props.label }}@if (props.required) {<span class="dd-required-star"> *</span>} </span>
      }

      @if (loading()) {
        <div class="flex items-center gap-2 p-4 border rounded-lg bg-light-tint">
          <mat-spinner diameter="24"></mat-spinner>
          <span class="text-sm">Ondertekendocument laden…</span>
        </div>
      } @else if (notFound()) {
        <div class="text-sm text-secondary p-4 border rounded bg-light-tint">Ondertekendocument niet gevonden.</div>
      } @else if (liveEntry(); as entry) {
        <div class="fuse-card bg-light-tint my-3 p-4 border">
          <div class="flex items-center justify-between w-full gap-4">
            <div class="flex items-center gap-4 min-w-0">
              <mat-icon class="text-hint">description</mat-icon>
              <div class="flex flex-col min-w-0">
                <span class="text-md font-medium truncate">{{ documentName() }}</span>
                <app-status-badge
                  class="self-start mt-1"
                  [label]="statusLabel()"
                  [variant]="statusBadgeVariant()"></app-status-badge>
              </div>
            </div>

            <div class="flex items-center gap-2 shrink-0">
              @if (documentMetadata()) {
                <button
                  mat-icon-button
                  (click)="downloadDocument()"
                  type="button"
                  matTooltip="Downloaden">
                  <mat-icon svgIcon="feather:download"></mat-icon>
                </button>
              }

              @if (canSign()) {
                <button mat-flat-button class="dd-action-button" type="button" (click)="openSignLink()">
                  <mat-icon class="icon-size-4" svgIcon="feather:edit-3"></mat-icon>
                  Ondertekenen
                </button>
              }
            </div>
          </div>

          @if (!submissionLink() && !isSigned()) {
            <div class="mt-3 text-sm text-hint">Ondertekenlink nog niet beschikbaar.</div>
          }
        </div>
      } @else {
        <div class="text-sm text-secondary p-4 border rounded bg-light-tint">Geen ondertekendocument gekoppeld.</div>
      }

      @if (props.description) {
        <div class="mt-2 text-sm text-accent">{{ props.description }}</div>
      }

      @if (field.formControl.errors) {
        <app-formly-error [field]="field"></app-formly-error>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldSigningSignComponent extends FieldType implements OnInit {
  readonly #modulesStore = inject(ModulesStore);
  readonly #entriesService = inject(EntriesService);
  readonly #signalRService = inject(SignalRService);
  readonly #destroyRef = inject(DestroyRef);

  readonly #liveEntry = signal<SigningEntry | null>(null);
  readonly loading = signal(false);
  readonly notFound = signal(false);

  readonly liveEntry = computed(() => this.#liveEntry());
  readonly #signingModuleId = computed(() => this.#modulesStore.moduleCodeToId('signing'));
  readonly #documentsModuleId = computed(() => this.#modulesStore.moduleCodeToId('documents'));

  /** Status slug, lowercase-normalized (backend webhook writes 'Verlopen' capitalized). */
  readonly statusSlug = computed<SigningStatus | null>(() => {
    const slug = this.#liveEntry()?.entry?.status?.slug;
    return (slug?.toLowerCase() as SigningStatus) ?? null;
  });
  readonly isSigned = computed(() => this.statusSlug() === SigningStatusEnum.Ondertekend);
  readonly submissionLink = computed(() => this.#liveEntry()?.entry?.submissionLink ?? null);
  /**
   * Read mode gate (#21239) — shared five-check shape, see `canMutateField`
   * (`readonly/readonly.types.ts`), plus the two signing-specific conditions below.
   *
   * `signing-sign` is an OWN-VIEW type outside `READONLY_KEEP_ENABLED_TYPES`, so read mode
   * stamps `props.readonly` AND `props.disabled` on it. Opening the sign link is a MUTATION —
   * the signer completes the document and the signing entry's status changes — so read mode
   * drops the affordance; the rest of the card (name, status badge, download) stays readable.
   *
   * A method, not a `computed`: `canMutateField` reads plain Formly objects, not signals, so a
   * computed would cache whatever it read on its first evaluation and never see a later stamp.
   * Called from the template it is re-evaluated on every change detection of this view, while
   * the signals it reads (`submissionLink`, `isSigned`) still register as view dependencies.
   */
  canSign(): boolean {
    return !!this.submissionLink() && !this.isSigned() && canMutateField(this.field);
  }

  readonly #documentEntry = computed(() => this.#liveEntry()?.entry?.document ?? null);
  readonly documentName = computed(
    () => this.#documentEntry()?.entry?.name ?? this.#liveEntry()?.entry?.name ?? 'Document'
  );
  readonly documentMetadata = computed<SigningDocumentMetadata | null>(() => {
    const documentEntry = this.#documentEntry();
    if (!documentEntry) return null;
    // Prefer the candidate that actually carries a documentId (the private/public
    // arrays can be present but empty/partial; the resolved id lives in entry.file).
    const candidates = [
      documentEntry.privateDocuments?.[0],
      documentEntry.publicDocuments?.[0],
      documentEntry.entry?.file,
    ];
    return candidates.find(meta => meta?.documentId) ?? candidates.find(Boolean) ?? null;
  });

  // Signer-facing labels. The backend status slugs (e.g. 'aanvraag-verstuurd')
  // describe the request from the sender's perspective; for the person who has to
  // sign we show what the status means for THEM. Slugs/taxonomy names are left
  // untouched — this is a display-only translation.
  readonly statusLabel = computed(() => {
    switch (this.statusSlug()) {
      case SigningStatusEnum.Nieuw:
        return 'In voorbereiding';
      case SigningStatusEnum.AanvraagVerstuurd:
        return 'Klaar om te ondertekenen';
      case SigningStatusEnum.Ondertekend:
        return 'Ondertekend';
      case SigningStatusEnum.Verlopen:
        return 'Verlopen';
      default:
        return this.#liveEntry()?.entry?.status?.name ?? 'Onbekend';
    }
  });

  /** #20811: badge variant for the generic <app-status-badge> — colors via --dd-badge-* tokens. */
  readonly statusBadgeVariant = computed<BadgeVariant>(() => {
    switch (this.statusSlug()) {
      case SigningStatusEnum.Ondertekend:
        return 'positive';
      case SigningStatusEnum.Verlopen:
        return 'negative';
      default:
        return 'warning';
    }
  });

  ngOnInit(): void {
    // The field is only valid once the signing entry is signed. It is display-only,
    // so its own formControl value stays empty — which means Formly's built-in
    // `required` validator (from props.required / the "*") would fail forever even
    // after signing. Replace the control's validators with our status-based gate so
    // validity tracks the signing status, not an empty value. The "*" label (driven
    // by props.required) is kept; only the value-based required check is dropped.
    this.field.formControl.setValidators(() => (this.isSigned() ? null : { signingNotComplete: true }));
    this.props.errorMessages = {
      ...(this.props.errorMessages ?? {}),
      signingNotComplete: () => 'Het document moet eerst ondertekend zijn.',
    };
    this.field.formControl.updateValueAndValidity({ emitEvent: false });

    this.#fetchEntry();

    // Refetch on changes to the signing entry itself (status updates) AND to the
    // host entry — the reference (e.g. entry.contract) is written there by an
    // action; SignalR updates the store but not the open form model, so without
    // this the contract would only appear after a manual page refresh. A bulk edit of
    // either module arrives as one message without entries (bulkEntriesChanged$) and
    // counts just the same.
    merge(this.#signalRService.entryChanged$, this.#signalRService.bulkEntriesChanged$)
      .pipe(
        filter(({ moduleId }) => moduleId === this.#signingModuleId() || moduleId === this.#hostModuleId()),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => this.#fetchEntry());

    // Value may arrive later (e.g. copied onto the entry by an action).
    this.field.formControl.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.#fetchEntry());
  }

  openSignLink(): void {
    // Re-check the gate the button already honours: the handler stays reachable from a stale
    // view (or a test/host calling it directly) after read mode stamped the field.
    if (!this.canSign()) return;
    const link = this.submissionLink();
    if (link) window.open(link, '_blank', 'noopener');
  }

  downloadDocument(): void {
    const documentEntry = this.#documentEntry();
    const metadata = this.documentMetadata();
    if (!documentEntry || !metadata) return;

    this.#modulesStore.downloadDocument({
      moduleId: this.#documentsModuleId(),
      entryId: documentEntry.id,
      documentId: metadata.documentId,
      documentName: metadata.documentName,
      blobUrl: metadata.blobUrl,
    });
  }

  /** Host (form) module id — the entry that holds the reference (e.g. onboarding). */
  #hostModuleId(): string | undefined {
    return this.options?.formState?.moduleId ?? this.options?.formState?.entity?.id;
  }

  #entryId(): string | null {
    // Read the signing-entry reference from `props.sourceFieldPath` when set
    // (the reference lives on another field after an action copied it), otherwise
    // the field's own value. Works for a SigningEntryRef or a full (copied) signing
    // entry — only `.id` is used.
    const sourceFieldPath = this.props.sourceFieldPath as string | undefined;
    if (!sourceFieldPath) {
      return this.field.formControl.value?.id ?? null;
    }

    const normalizedPath = sourceFieldPath.startsWith('entry.') ? sourceFieldPath.substring(6) : sourceFieldPath;

    // Freshest source: the host entry in the store, which SignalR updates before
    // entryChanged$ fires — the open form model is not reliably re-synced. Fall
    // back to formState.hostEntry (the onboarding-status template resolves its
    // entry by URL param, so it never reaches the store and formState.entry holds
    // only the live form model), then formState entry, then the form model.
    const hostModuleId = this.#hostModuleId();
    const hostEntryId = this.options?.formState?.entry?.id;
    const storeEntry =
      hostModuleId && hostEntryId
        ? this.#modulesStore.entries()[hostModuleId]?.find(entry => entry.id === hostEntryId)
        : undefined;

    const data =
      storeEntry?.entry ??
      this.options?.formState?.hostEntry?.entry ??
      this.options?.formState?.entry?.entry ??
      this.model ??
      {};
    return get(data, normalizedPath)?.id ?? null;
  }

  #fetchEntry(): void {
    const entryId = this.#entryId();
    const moduleId = this.#signingModuleId();
    if (!entryId || !moduleId) {
      this.#updateValidity();
      return;
    }

    this.loading.set(this.#liveEntry() === null);
    this.#entriesService.getEntry(moduleId, entryId).subscribe({
      next: (entry: SigningEntry) => {
        this.#liveEntry.set(entry);
        this.notFound.set(false);
        this.loading.set(false);
        this.#updateValidity();
      },
      error: error => {
        debugLog('signing-sign', `failed to fetch signing entry ${entryId}`, error);
        this.notFound.set(true);
        this.loading.set(false);
        this.#updateValidity();
      },
    });
  }

  #updateValidity(): void {
    // Re-run the registered validator against the latest status. emitEvent:false
    // avoids re-triggering valueChanges (which would loop back into #fetchEntry).
    this.field.formControl.updateValueAndValidity({ emitEvent: false });
  }
}
